import { Component, Output, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { environment } from 'src/environments/environment';

import { GoogleMapsLoaderService } from 'src/app/google-maps-loader.service';

@Component({
  selector: 'google-address-search',
  templateUrl: './google-address-search.component.html',
  styleUrls: ['./google-address-search.component.scss']
})
export class GoogleAddressSearchComponent {
  @Output() selectedAddressChanged = new EventEmitter();
  @ViewChild('googleMapsAutocomplete') googleMapsAutocompleteRef!: ElementRef;
  _debouncer_duration_in_ms_ = 1000; // _CONST_
  searchString: string = '';
  suggestedAddresses = [];
  isSearchingApi = false;
  SearchFG: FormGroup;

  googleMapsAutocomplete: google.maps.places.Autocomplete | null = null;

  constructor(private _formBuilder: FormBuilder, private googleMapsLoader: GoogleMapsLoaderService) {
    this.SearchFG = this._formBuilder
      .group({
        searchString: [{ value: this.searchString, disabled: false }, Validators.required]
      });
  }

  ngAfterViewInit(): void {
    this.googleMapsLoader
      .loadGoogleMaps()
      .then(() => this.initializeAutocomplete())
      .catch((error) => { throw error });
  }

  ngOnDestroy(): void {
    this.googleMapsAutocomplete?.unbindAll();
  }

  mapPlaceResultToAddressFormValue(place: google.maps.places.PlaceResult) {
    const address = {
      Street: '',
      Number: '',
      BoxNumber: '',
      ApartmentNumber: '',
      PostalCode: null,
      City: '',
      Latitude: null,
      Longitude: null
    };

    if (!place.geometry?.location)
        throw new Error('PlaceResult must contain geometry with location');

    address.Latitude = place.geometry.location.lat();
    address.Longitude = place.geometry.location.lng();

    if (place.address_components) {
      place.address_components.forEach(component => {
        const types = component.types;
        if (types.includes('route')) address.Street = component.long_name;
        if (types.includes('street_number')) address.Number = component.short_name;
        if (types.includes('postal_code')) address.PostalCode = component.short_name;
        if (types.includes('locality')) address.City = component.long_name;
        // BoxNumber & ApartmentNumber are not provided by Google Maps
      });
    }

    return address;
  }

  initializeAutocomplete() {
    const inputElement = this.googleMapsAutocompleteRef.nativeElement;

    // Initialize Google Maps Autocomplete for each field
    this.googleMapsAutocomplete = new google.maps.places.Autocomplete(inputElement, {
      types: ['address'],
      componentRestrictions: { country: 'BE' } // Restrict to Belgium
    });

    // Add listener for place_changed
    this.googleMapsAutocomplete.addListener('place_changed', () => {
      const placeResult = this.googleMapsAutocomplete.getPlace();
      try {
        const address = this.mapPlaceResultToAddressFormValue(placeResult);
        this.SearchFG.reset(address);
        this.selectedAddressChanged.emit(address);
      }
      catch (e) { throw e }
    });
  }

  performSearchApi = async (searchAddress: string) => {
    const googleGeocoderApiUrl = new URL(`https://maps.googleapis.com/maps/api/geocode/json`);
    [ // map to add params to URL
      { param: 'address', value: searchAddress },
      { param: 'key', value: environment?.googleMapsApiKey || '' },
      { param: 'components', value: 'country:BE' }
    ].forEach(({ param, value }) => googleGeocoderApiUrl.searchParams.append(param, value));
    this.isSearchingApi = true
    try {
      fetch(googleGeocoderApiUrl.toString())
        .then(json => json ? json.json() : [])
        .then((addresses: Array<{}>) => this.suggestedAddresses = addresses || []);
    }
    catch (err) { throw err; }
    finally { this.isSearchingApi = false }
  };
}
